import { useEffect, useMemo, useState } from "react";
import { Download, ArrowUpDown, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormAlert } from "@/components/common/Alert";
import { EmptyState } from "@/components/common/EmptyState";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage } from "@/lib/api";
import { targetName } from "@/lib/catalogue";
import { formatDateTime } from "@/lib/format";

const FRAMES = [["7d", "Last 7 days"], ["30d", "Last 30 days"], ["month", "This month"], ["last_month", "Last month"], ["quarter", "This quarter"], ["custom", "Custom range"]];
const iso = (d) => d.toISOString().slice(0, 10);

export function rangeFor(frame, custom) {
  const n = new Date();
  const today = new Date(n.getFullYear(), n.getMonth(), n.getDate());
  const tomorrow = new Date(today.getTime() + 86400000);
  if (frame === "7d") return [new Date(tomorrow - 7 * 86400000), tomorrow];
  if (frame === "30d") return [new Date(tomorrow - 30 * 86400000), tomorrow];
  if (frame === "month") return [new Date(n.getFullYear(), n.getMonth(), 1), tomorrow];
  if (frame === "last_month") return [new Date(n.getFullYear(), n.getMonth() - 1, 1), new Date(n.getFullYear(), n.getMonth(), 1)];
  if (frame === "quarter") return [new Date(n.getFullYear(), Math.floor(n.getMonth() / 3) * 3, 1), tomorrow];
  const s = custom.from ? new Date(`${custom.from}T00:00:00`) : today;
  const e = custom.to ? new Date(new Date(`${custom.to}T00:00:00`).getTime() + 86400000) : tomorrow;
  return [s, e];
}

const COLS = [
  ["name", "Person"], ["agent_started", "Agent runs started"], ["agent_finished", "Finished"], ["agent_abandoned", "Abandoned"],
  ["workflow_started", "Workflow runs started"], ["workflow_finished", "Finished"], ["workflow_abandoned", "Abandoned"],
  ["prompts_added", "Prompts added"], ["prompts_copied", "Prompts copied"], ["feedback_given", "Feedback given"], ["last_active", "Last active"],
];
const flat = (r) => ({
  id: r.id, name: r.name, email: r.email, status: r.status,
  agent_started: r.runs.agent.started, agent_finished: r.runs.agent.finished, agent_abandoned: r.runs.agent.abandoned,
  workflow_started: r.runs.workflow.started, workflow_finished: r.runs.workflow.finished, workflow_abandoned: r.runs.workflow.abandoned,
  prompts_added: r.prompts_added, prompts_copied: r.prompts_copied, feedback_given: r.feedback_given, last_active: r.last_active,
});

function exportCsv(rows, label) {
  const head = COLS.map(([, l], i) => (i >= 2 && i <= 3 ? `Agent ${l.toLowerCase()}` : i >= 5 && i <= 6 ? `Workflow ${l.toLowerCase()}` : l));
  const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [head.join(","), ...rows.map((r) => COLS.map(([k]) => esc(k === "last_active" ? (r[k] ? formatDateTime(r[k]) : "") : r[k])).join(","))];
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `fcg-hub-activity-${label}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const PersonDetail = ({ person, range, onClose }) => {
  const { agentsByKey, workflowsByKey } = useCatalogue();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!person) return;
    setRows(null);
    api.get(`/admin/activity/${person.id}`, { params: { start: range[0].toISOString(), end: range[1].toISOString() } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [person, range]);
  const max = Math.max(1, ...(rows || []).map((r) => r.started));
  return (
    <Dialog open={!!person} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl sm:max-w-2xl" data-testid="activity-person-dialog">
        <DialogHeader><DialogTitle>{person?.name}</DialogTitle><DialogDescription>Runs by agent and workflow for the same period. Counts only.</DialogDescription></DialogHeader>
        {!rows ? <Skeleton className="h-32 rounded-xl" /> : rows.length === 0 ? <p className="text-sm text-muted-foreground">No runs in this period.</p> : (
          <>
            <div className="space-y-2" aria-hidden="true" data-testid="activity-person-chart">
              {rows.slice(0, 10).map((r) => (
                <div key={r.target_id} className="flex items-center gap-3 text-sm">
                  <span className="w-36 truncate">{targetName(r.target_id, agentsByKey, workflowsByKey)}</span>
                  <span className="h-3 rounded-full bg-action" style={{ width: `${(r.started / max) * 60}%`, minWidth: 6 }} />
                  <span className="tabular-nums text-muted-foreground">{r.started}</span>
                </div>
              ))}
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>Agent or workflow</TableHead><TableHead className="text-right">Started</TableHead><TableHead className="text-right">Finished</TableHead><TableHead className="text-right">Abandoned</TableHead></TableRow></TableHeader>
              <TableBody>{rows.map((r) => <TableRow key={r.target_id} data-testid={`activity-person-row-${r.target_id}`}><TableCell>{targetName(r.target_id, agentsByKey, workflowsByKey)} <span className="text-xs text-muted-foreground">{r.target_type === "workflow" ? "Workflow" : "Agent"}</span></TableCell><TableCell className="text-right tabular-nums">{r.started}</TableCell><TableCell className="text-right tabular-nums">{r.finished}</TableCell><TableCell className="text-right tabular-nums">{r.abandoned}</TableCell></TableRow>)}</TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function ActivityAdmin() {
  const [frame, setFrame] = useState("30d");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ key: "agent_started", dir: -1 });
  const [person, setPerson] = useState(null);
  const range = useMemo(() => rangeFor(frame, custom), [frame, custom]);

  useEffect(() => {
    setRows(null);
    api.get("/admin/activity", { params: { start: range[0].toISOString(), end: range[1].toISOString() } })
      .then(({ data }) => { setRows(data.map(flat)); setError(""); }).catch((e) => { setError(errorMessage(e)); setRows([]); });
  }, [range]);

  const sorted = useMemo(() => [...(rows || [])].sort((a, b) => {
    const x = a[sort.key] ?? "";
    const y = b[sort.key] ?? "";
    return (x > y ? 1 : x < y ? -1 : 0) * sort.dir;
  }), [rows, sort]);
  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : key === "name" ? 1 : -1 }));

  return (
    <div className="space-y-5" data-testid="admin-activity">
      <p className="text-sm text-muted-foreground">How many runs each person starts, and on which agents. Counts only: never a run label, a campaign or anything a run produced.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="activity-frame">Timeframe</Label>
          <Select value={frame} onValueChange={setFrame}>
            <SelectTrigger id="activity-frame" className="h-10 w-[190px] rounded-xl bg-card" data-testid="activity-timeframe"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-xl">{FRAMES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`activity-frame-${v}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {frame === "custom" && (
          <>
            <div className="space-y-1.5"><Label htmlFor="activity-from">From</Label><Input id="activity-from" type="date" value={custom.from} max={custom.to || undefined} onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="activity-from" /></div>
            <div className="space-y-1.5"><Label htmlFor="activity-to">To</Label><Input id="activity-to" type="date" value={custom.to} min={custom.from || undefined} onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="activity-to" /></div>
          </>
        )}
        <Button variant="secondary" className="ml-auto" disabled={!rows?.length} onClick={() => exportCsv(sorted, `${iso(range[0])}-to-${iso(new Date(range[1] - 86400000))}`)} data-testid="activity-export"><Download className="!h-4 !w-4" /> Export CSV</Button>
      </div>
      <FormAlert>{error}</FormAlert>
      {!rows ? <Skeleton className="h-48 rounded-xl" /> : rows.length === 0 ? <EmptyState icon={Users} title="No people yet" body="Invite someone from Users to see activity here." testid="activity-empty" /> : (
        <div className="card-elevated overflow-x-auto">
          <Table>
            <TableHeader><TableRow>{COLS.map(([k, l]) => (
              <TableHead key={k} className={k === "name" ? "" : "text-right"} aria-sort={sort.key === k ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
                <button type="button" onClick={() => toggle(k)} className="inline-flex items-center gap-1 whitespace-nowrap hover:text-foreground" data-testid={`activity-sort-${k}`}>{l}<ArrowUpDown className="!h-3.5 !w-3.5" aria-hidden="true" /></button>
              </TableHead>
            ))}</TableRow></TableHeader>
            <TableBody>{sorted.map((r) => (
              <TableRow key={r.id} data-testid={`activity-row-${r.email}`}>
                <TableCell><button type="button" onClick={() => setPerson(r)} className="text-left font-medium underline-offset-4 hover:underline" data-testid={`activity-person-${r.email}`}>{r.name}</button>{r.status !== "active" && <span className="ml-2 text-xs text-muted-foreground">{r.status}</span>}</TableCell>
                {COLS.slice(1, -1).map(([k]) => <TableCell key={k} className="text-right tabular-nums">{r[k]}</TableCell>)}
                <TableCell className="whitespace-nowrap text-right text-sm text-muted-foreground">{r.last_active ? formatDateTime(r.last_active) : "Never"}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
      <PersonDetail person={person} range={range} onClose={() => setPerson(null)} />
    </div>
  );
}
